/**
 * 콘솔 권한 모델 — 영역·레벨·역할·사용자 타입 + 판정 함수 (BOOTH-121).
 * 서버 게이트와 콘솔 UI 가 같은 판정을 내리도록 모든 규칙이 여기 산다.
 */
// ─── 영역·레벨 상수 ──────────────────────────────────────────────────────────
export const PERMISSION_AREAS = [
    "content",
    "library",
    "motions",
    "copy",
    "presets",
    "branding",
    "gallery",
    "dashboard",
    "logs",
    "payments",
    "display",
    "sync",
    "overview",
    "booths",
    "privacy",
    "photo-model",
    "video-model",
    "print-queue",
    "accounts",
    // 계정과 역할은 별도 영역이다 (사용자 결정 2026-10-23) — 역할 편집은 권한 표 자체를 바꾸므로
    // 계정 관리보다 민감하다. `GET /roles` 만 예외로 accounts view 로도 읽을 수 있다(계정 화면의 역할 Select).
    "roles",
    // ADMIN 전용 저장소 파일 보관함 — 시스템 메뉴.
    "files",
    // 감사 로그 — 부스·계정 삭제 이력 조회 (BOOTH-131). 시스템 그룹 끝.
    "audit",
];
export const PERMISSION_LEVELS = ["none", "view", "edit"];
// ─── 역할 ID 상수 ─────────────────────────────────────────────────────────────
export const ADMIN_ROLE_ID = "role_admin";
export const MANAGER_ROLE_ID = "role_manager";
export const STAFF_ROLE_ID = "role_staff";
// ─── 레벨 판정 함수 ───────────────────────────────────────────────────────────
const LEVEL_ORDER = { none: 0, view: 1, edit: 2 };
/** actual 이 required 이상이면 true. */
export function levelAtLeast(actual, required) {
    return LEVEL_ORDER[actual] >= LEVEL_ORDER[required];
}
/** area 에 view 이상 권한이 있으면 true. */
export function canView(p, area) {
    return levelAtLeast(p[area], "view");
}
/** area 에 edit 권한이 있으면 true. */
export function canEdit(p, area) {
    return levelAtLeast(p[area], "edit");
}
// ─── normalizePermissions ─────────────────────────────────────────────────────
/**
 * partial 입력을 Permissions 로 정규화한다.
 * 누락된 키와 잘못된 값은 "none" 으로, 알 수 없는 키는 버린다.
 */
export function normalizePermissions(partial) {
    const result = {};
    for (const area of PERMISSION_AREAS) {
        const v = partial[area];
        result[area] = PERMISSION_LEVELS.includes(v)
            ? v
            : "none";
    }
    return result;
}
// ─── ADMIN_PERMISSIONS ────────────────────────────────────────────────────────
/** 모든 영역이 edit 인 권한 집합. */
export const ADMIN_PERMISSIONS = normalizePermissions(Object.fromEntries(PERMISSION_AREAS.map((a) => [a, "edit"])));
// ─── permissionsCover ─────────────────────────────────────────────────────────
/** 모든 영역에서 a >= b 이면 true. */
export function permissionsCover(a, b) {
    for (const area of PERMISSION_AREAS) {
        if (!levelAtLeast(a[area], b[area]))
            return false;
    }
    return true;
}
// ─── DEFAULT_ROLES ────────────────────────────────────────────────────────────
/**
 * 기본 역할 3종 (ADMIN / MANAGER / STAFF).
 * §4.1·§4.2 표 그대로 — createdAt·updatedAt 은 레포가 채운다.
 */
export const DEFAULT_ROLES = [
    {
        id: ADMIN_ROLE_ID,
        name: "ADMIN",
        description: "개발자 — 모든 권한, 계정·시스템 역할 관리",
        permissions: ADMIN_PERMISSIONS,
        isSystem: true,
    },
    {
        id: MANAGER_ROLE_ID,
        name: "MANAGER",
        description: "고객사 운영자 — 사진·영상 모델은 보기만",
        permissions: normalizePermissions(Object.fromEntries(PERMISSION_AREAS.map((a) => [
            a,
            a === "files" ? "none" : a === "photo-model" || a === "video-model" || a === "audit" ? "view" : "edit",
        ]))),
    },
    {
        id: STAFF_ROLE_ID,
        name: "STAFF",
        description: "현장 직원 — 갤러리·디스플레이·동기화·인쇄 큐 수정, 나머지 보기",
        permissions: normalizePermissions(Object.fromEntries(PERMISSION_AREAS.map((a) => {
            if (a === "accounts" || a === "roles" || a === "audit" || a === "files")
                return [a, "none"];
            if (a === "gallery" ||
                a === "display" ||
                a === "sync" ||
                a === "print-queue") {
                return [a, "edit"];
            }
            return [a, "view"];
        }))),
    },
];
// ─── isAdminRole ──────────────────────────────────────────────────────────────
/** ADMIN_ROLE_ID 와 같으면 true. */
export function isAdminRole(roleId) {
    return roleId === ADMIN_ROLE_ID;
}
// ─── boothAllowed / filterBoothsForUser ───────────────────────────────────────
/**
 * 사용자가 boothId 에 접근할 수 있으면 true.
 * boothIds 가 없거나 빈 배열이면 전체 허용.
 */
export function boothAllowed(user, boothId) {
    if (!user.boothIds || user.boothIds.length === 0)
        return true;
    return user.boothIds.includes(boothId);
}
/** 사용자 boothIds 로 부스 목록을 필터한다. boothIds 없음/빈 배열이면 전체 반환. */
export function filterBoothsForUser(user, booths) {
    if (!user.boothIds || user.boothIds.length === 0)
        return booths;
    return booths.filter((b) => user.boothIds.includes(b.id));
}
/**
 * status === "ready" 이고 canView 인 첫 번째 path 를 반환한다.
 * 없으면 null.
 */
export function firstVisiblePath(p, items) {
    for (const item of items) {
        if (item.status === "ready" && canView(p, item.area)) {
            return item.path;
        }
    }
    return null;
}
// ─── BOOTH_FIELD_AREAS ────────────────────────────────────────────────────────
/**
 * Booth PATCH 필드 → 필요 권한 영역 매핑.
 * 표에 없는 키는 ["booths"] 로 처리한다 (requiredAreasForBoothPatch 참고).
 */
export const BOOTH_FIELD_AREAS = {
    sampleVideoUrl: ["content"],
    musicUrl: ["content"],
    captionStyleId: ["content"],
    paperMm: ["presets", "print-queue"],
    printMode: ["print-queue"],
    /** 워터마크 메뉴(`/watermark`, 영역 branding) 와 부스 관리 양쪽에서 저장 가능 (BOOTH-128). */
    watermark: ["branding", "booths"],
};
// ─── requiredAreasForBoothPatch / boothPatchAllowed ───────────────────────────
/**
 * PATCH 본문의 키마다 허용 영역 목록을 반환한다.
 * 표에 없는 키는 ["booths"].
 * 빈 배열 입력 시 빈 배열 반환.
 */
export function requiredAreasForBoothPatch(keys) {
    return keys.map((k) => BOOTH_FIELD_AREAS[k] ?? ["booths"]);
}
/**
 * 모든 키에 대해 "허용 영역 목록 중 하나라도 edit 권한이 있으면 통과" 를 만족하면 true.
 * 빈 키 배열은 false (라우트가 빈 본문을 400 으로 처리하므로 여기서 통과시키지 않는다).
 */
export function boothPatchAllowed(p, keys) {
    if (keys.length === 0)
        return false;
    const areas = requiredAreasForBoothPatch(keys);
    return areas.every((list) => list.some((area) => canEdit(p, area)));
}
//# sourceMappingURL=access.js.map